import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import {
  traiComplianceService, Complaint, ComplaintStatus, COMPLAINT_STATUSES, RiskView, ComplianceAlert,
} from 'api/services/traiCompliance.service';
import {
  COMPLIANCE_ADMINS, PLATFORM_ROLES, LEVEL_LABEL, daysLeft, formatWhen, hasRole, isOverdue, levelTone, localInputToIso,
} from './traiRules';
import { Card, ImportPanel, Note, Pill, inputCls, labelCls, muted, th, td } from './shared';

/**
 * Item 4: UCC complaints the access provider reports against our numbers, and where each number
 * and the organisation stand against reg 25(5)(d) (3 unique complainants + AI flag, or 5, in 10
 * days) and 21A (5 AI-flagged numbers in 10 days). Computed only: nothing is suspended by cpass,
 * and alerts are stored, never emailed.
 */
const ComplaintsTab: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const canEdit = hasRole(role, COMPLIANCE_ADMINS);
  const platform = hasRole(role, PLATFORM_ROLES);

  const [all, setAll] = useState(false);
  const [risk, setRisk] = useState<RiskView | null>(null);
  const [rows, setRows] = useState<Complaint[]>([]);
  const [total, setTotal] = useState(0);
  const [alerts, setAlerts] = useState<ComplianceAlert[]>([]);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, c, a] = await Promise.all([
      traiComplianceService.risk(), traiComplianceService.complaints(all), traiComplianceService.alerts(all),
    ]);
    if (r.success && r.data) setRisk(r.data);
    if (c.success && c.data) { setRows(c.data.content || []); setTotal(c.data.totalElements || 0); }
    else if (!c.success) setNote({ ok: false, text: c.error || 'Could not load complaints' });
    if (a.success && a.data) setAlerts(a.data.content || []);
    setLoading(false);
  }, [all]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (c: Complaint, status: ComplaintStatus) => {
    const r = await traiComplianceService.updateComplaint(c.id, { status });
    setNote(r.success ? { ok: true, text: `${c.complaintRef}: ${status}` } : { ok: false, text: r.error || 'Could not update' });
    load();
  };

  const toggleFlag = async (cli: string, on: boolean) => {
    const r = await traiComplianceService.setCliFlag(cli, { aiFlagged: on });
    setNote(r.success ? { ok: true, text: `${cli} ${on ? 'marked' : 'unmarked'} AI-flagged` } : { ok: false, text: r.error || 'Failed' });
    load();
  };

  const overdue = rows.filter((c) => isOverdue(c)).length;

  return (
    <div className="space-y-4">
      <Note note={note} />

      <Card title="Risk — rolling window"
            subtitle={risk ? `Thresholds: ${risk.thresholds.uniqueWithFlag} unique complainants with an AI flag, or ${risk.thresholds.uniqueAlone} alone, in ${risk.thresholds.windowDays} days (reg 25(5)(d)); ${risk.thresholds.kycFlaggedClis} AI-flagged numbers (reg 21A).` : ''}
            right={<button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={load}><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>}>
        {!risk ? <p className="text-sm" style={muted}>{loading ? 'Loading…' : 'No data.'}</p> : (
          <>
            <div className="flex flex-wrap gap-2 text-sm">
              <Pill tone={risk.senderBarRisk ? 'danger' : 'success'}>
                {risk.senderBarRisk ? 'A number meets the suspension threshold' : 'No number at the suspension threshold'}
              </Pill>
              <Pill tone={risk.kycReverification ? 'danger' : 'success'}>
                {risk.aiFlaggedClis} AI-flagged number(s){risk.kycReverification ? ' — KYC re-verification threshold met' : ''}
              </Pill>
              <Pill tone="info">{risk.complaints} complaint(s) in window</Pill>
            </div>
            <p className="text-xs" style={muted}>{risk.detail}. {risk.enforcement}.</p>
            {risk.clis.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr>
                    <th className={th}>Number</th><th className={th}>Unique complainants</th><th className={th}>AI-flagged</th>
                    <th className={th}>Standing</th>
                  </tr></thead>
                  <tbody>
                    {risk.clis.map((c) => (
                      <tr key={c.cli} className="border-t border-gray-100 dark:border-gray-800">
                        <td className={`${td} font-mono`}>{c.cli}</td>
                        <td className={td}>{c.uniqueComplainants} <span style={muted}>({c.complaints} complaints)</span></td>
                        <td className={td}>
                          {canEdit ? (
                            <label className="inline-flex items-center gap-1 text-sm">
                              <input type="checkbox" checked={c.aiFlagged} onChange={(e) => toggleFlag(c.cli, e.target.checked)} /> flagged
                            </label>
                          ) : (c.aiFlagged ? 'Yes' : 'No')}
                        </td>
                        <td className={td}>
                          <Pill tone={levelTone(c.level)}>{LEVEL_LABEL[c.level] || c.level}</Pill>
                          <div className="text-[11px] mt-1" style={muted}>{c.detail}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {canEdit && <FlagForm onSaved={load} />}

      <Card title={`Complaints (${total})`} subtitle={overdue ? `${overdue} open complaint(s) past the evidence deadline.` : 'Evidence is due 5 days after a complaint is received.'}
            right={platform ? (
              <label className="inline-flex items-center gap-1 text-sm">
                <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> All organisations
              </label>
            ) : undefined}>
        {rows.length === 0 ? <p className="text-sm" style={muted}>{loading ? 'Loading…' : 'No complaints.'}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <th className={th}>Reference</th><th className={th}>Received</th><th className={th}>Number</th>
                <th className={th}>Complainant</th><th className={th}>Category / circle</th><th className={th}>Evidence due</th>
                <th className={th}>Status</th>
              </tr></thead>
              <tbody>
                {rows.map((c) => {
                  const late = isOverdue(c);
                  return (
                    <tr key={c.id} className={`border-t border-gray-100 dark:border-gray-800 ${late ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                      <td className={`${td} font-mono`}>{c.complaintRef}{all && <div className="text-[11px]" style={muted}>{c.organizationId}</div>}</td>
                      <td className={td}>{formatWhen(c.receivedAt)}</td>
                      <td className={`${td} font-mono`}>{c.cli}</td>
                      <td className={`${td} font-mono`}>{c.complainant || '—'}</td>
                      <td className={td}>{c.category || '—'} / {c.circle || '—'}</td>
                      <td className={td}>
                        {formatWhen(c.evidenceDueAt)}
                        {c.status === 'OPEN' && (
                          <div className={`text-[11px] ${late ? 'text-red-700 font-medium' : ''}`} style={late ? undefined : muted}>
                            {late ? `Overdue by ${Math.abs(daysLeft(c.evidenceDueAt))} day(s)` : `${daysLeft(c.evidenceDueAt)} day(s) left`}
                          </div>
                        )}
                      </td>
                      <td className={td}>
                        {canEdit ? (
                          <select className="tl-input" value={c.status} onChange={(e) => setStatus(c, e.target.value as ComplaintStatus)}>
                            {COMPLAINT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : c.status}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {canEdit && <ComplaintForm onSaved={(t) => { setNote({ ok: true, text: t }); load(); }} />}

      {canEdit && (
        <Card title="Import complaints (CSV)" subtitle="Always previewed first. Re-importing a reference updates it.">
          <ImportPanel
            columns="complaint_ref,received_at,complainant,cli,category,circle,status,notes"
            example="URN-2026-0001,2026-11-18,+919876543210,+912265197304,PROMOTIONAL,MH,OPEN,"
            run={traiComplianceService.importComplaints}
            onApplied={load}
          />
        </Card>
      )}

      <Card title="Stored alerts" subtitle="Recorded only — never emailed or sent.">
        {alerts.length === 0 ? <p className="text-sm" style={muted}>No alerts.</p> : (
          <ul className="text-sm space-y-1">
            {alerts.map((a) => (
              <li key={a.id}>
                <Pill tone={a.kind === 'CLI_WARNING' ? 'amber' : 'danger'}>{a.kind}</Pill>{' '}
                <span className="font-mono">{a.subject}</span> <span style={muted}>{a.windowKey}</span> — {a.detail}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

const FlagForm: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const [cli, setCli] = useState('');
  const [flagRef, setFlagRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    const r = await traiComplianceService.setCliFlag(cli, { aiFlagged: true, flagRef: flagRef || undefined });
    setBusy(false);
    if (r.success) { setCli(''); setFlagRef(''); onSaved(); } else setErr(r.error || 'Could not save');
  };
  return (
    <Card title="Mark a number AI-flagged" subtitle="As reported by the access provider. Lowers the suspension threshold from 5 to 3.">
      <div className="flex gap-2 flex-wrap">
        <input className="tl-input" placeholder="Our number, +91…" value={cli} onChange={(e) => setCli(e.target.value)} />
        <input className="tl-input" placeholder="Provider reference (optional)" value={flagRef} onChange={(e) => setFlagRef(e.target.value)} />
        <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={!cli.trim() || busy} onClick={save}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Mark flagged'}
        </button>
      </div>
      {err && <p className="text-sm text-red-700">{err}</p>}
    </Card>
  );
};

const ComplaintForm: React.FC<{ onSaved: (t: string) => void }> = ({ onSaved }) => {
  const blank = { complaint_ref: '', received_at: '', complainant: '', cli: '', category: '', circle: '', notes: '' };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    const body: Record<string, any> = {};
    Object.entries(f).forEach(([k, v]) => { if (v.trim()) body[k] = v.trim(); });
    body.received_at = localInputToIso(f.received_at);
    const r = await traiComplianceService.upsertComplaint(body);
    setBusy(false);
    if (r.success) { setF(blank); onSaved(`Saved ${r.data?.complaintRef || 'complaint'}`); } else setErr(r.error || 'Could not save');
  };
  const field = (k: keyof typeof blank, label: string, type = 'text') => (
    <label><span className={labelCls} style={muted}>{label}</span>
      <input type={type} className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
  );
  return (
    <Card title="Record a complaint" subtitle="The complainant's number is stored hashed with the last four digits only.">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {field('complaint_ref', 'Reference / URN')}
        {field('received_at', 'Received', 'datetime-local')}
        {field('cli', 'Our number')}
        {field('complainant', 'Complainant number')}
        <label><span className={labelCls} style={muted}>Category</span>
          <select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
            <option value="">—</option><option value="PROMOTIONAL">Promotional</option><option value="SERVICE">Service</option>
          </select></label>
        {field('circle', 'Circle')}
      </div>
      <label className="block"><span className={labelCls} style={muted}>Notes</span>
        <textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>
      {err && <p className="text-sm text-red-700">{err}</p>}
      <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={busy || !f.complaint_ref.trim() || !f.cli.trim() || !f.received_at} onClick={save}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save complaint'}
      </button>
    </Card>
  );
};

export default ComplaintsTab;
